import React, { useState } from "react";
import { FaLock, FaEye, FaEyeSlash, FaCheck, FaExclamationCircle, FaShieldAlt } from "react-icons/fa";
import { apiService } from "../../../components/Config/api.service";

const Security = () => {
    const [passwordFields, setPasswordFields] = useState({
        current: "",
        new: "",
        confirm: "",
    });
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordMsg, setPasswordMsg] = useState({ type: "", text: "" });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false,
    });

    const handlePasswordChange = (e) => {
        setPasswordFields({ ...passwordFields, [e.target.name]: e.target.value });
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
    };

    const getPasswordStrength = (password) => {
        if (!password) return { strength: 0, label: "", color: "" };
        
        let strength = 0;
        if (password.length >= 8) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/\d/.test(password)) strength++;
        if (/[^A-Za-z0-9]/.test(password)) strength++;

        const levels = [
            { strength: 0, label: "", color: "" },
            { strength: 1, label: "Très faible", color: "bg-red-500" },
            { strength: 2, label: "Faible", color: "bg-orange-500" },
            { strength: 3, label: "Moyen", color: "bg-yellow-500" },
            { strength: 4, label: "Fort", color: "bg-blue-500" },
            { strength: 5, label: "Très fort", color: "bg-green-500" },
        ];

        return levels[strength];
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setPasswordMsg({ type: "", text: "" });
        
        if (!passwordFields.current || !passwordFields.new || !passwordFields.confirm) {
            setPasswordMsg({ type: "error", text: "Tous les champs sont obligatoires." });
            return;
        }
        
        if (passwordFields.new !== passwordFields.confirm) {
            setPasswordMsg({ type: "error", text: "Les nouveaux mots de passe ne correspondent pas." });
            return;
        }

        if (passwordFields.new.length < 8) {
            setPasswordMsg({ type: "error", text: "Le nouveau mot de passe doit contenir au moins 8 caractères." });
            return;
        }
        
        setPasswordLoading(true);
        try {
            await apiService.saveElement("/user/change_password", {
                current_password: passwordFields.current,
                new_password: passwordFields.new,
            });
            setPasswordMsg({ type: "success", text: "Mot de passe mis à jour avec succès !" });
            setPasswordFields({ current: "", new: "", confirm: "" });
        } catch (err) {
            setPasswordMsg({
                type: "error",
                text: err?.userMessage || "Erreur lors de la mise à jour du mot de passe.",
            });
        }
        setPasswordLoading(false);
    };

    const passwordStrength = getPasswordStrength(passwordFields.new);

    return (
        <form onSubmit={handlePasswordSubmit} className="space-y-8">
            {/* Section Mot de passe */}
            <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                    <div className="bg-red-100 rounded-full p-2">
                        <FaShieldAlt className="text-red-600" size={16} />
                    </div>
                    <div>
                        <h4 className="text-lg font-semibold text-gray-800">Changement de mot de passe</h4>
                        <p className="text-sm text-gray-600">Assurez-vous d'utiliser un mot de passe fort et unique</p>
                    </div>
                </div>

                <div className="space-y-6">
                    {/* Mot de passe actuel */}
                    <div>
                        <label htmlFor="current" className="block text-sm font-medium text-gray-700 mb-2">
                            Mot de passe actuel *
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FaLock className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                                id="current"
                                name="current"
                                type={showPasswords.current ? "text" : "password"}
                                value={passwordFields.current}
                                onChange={handlePasswordChange}
                                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                                placeholder="Votre mot de passe actuel"
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => togglePasswordVisibility('current')}
                            >
                                {showPasswords.current ? (
                                    <FaEyeSlash className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                                ) : (
                                    <FaEye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Nouveau mot de passe */}
                    <div>
                        <label htmlFor="new" className="block text-sm font-medium text-gray-700 mb-2">
                            Nouveau mot de passe *
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FaLock className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                                id="new"
                                name="new"
                                type={showPasswords.new ? "text" : "password"}
                                value={passwordFields.new}
                                onChange={handlePasswordChange}
                                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                                placeholder="Votre nouveau mot de passe"
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => togglePasswordVisibility('new')}
                            >
                                {showPasswords.new ? (
                                    <FaEyeSlash className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                                ) : (
                                    <FaEye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                                )}
                            </button>
                        </div>
                        
                        {/* Indicateur de force du mot de passe */}
                        {passwordFields.new && (
                            <div className="mt-2">
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                                        <div 
                                            className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                                            style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                                        ></div>
                                    </div>
                                    <span className="text-xs text-gray-600">{passwordStrength.label}</span>
                                </div>
                                <div className="text-xs text-gray-500 space-y-1">
                                    <p>Le mot de passe doit contenir :</p>
                                    <ul className="list-disc list-inside space-y-0.5 ml-2">
                                        <li className={passwordFields.new.length >= 8 ? "text-green-600" : "text-gray-400"}>
                                            Au moins 8 caractères
                                        </li>
                                        <li className={/[A-Z]/.test(passwordFields.new) ? "text-green-600" : "text-gray-400"}>
                                            Une lettre majuscule
                                        </li>
                                        <li className={/[a-z]/.test(passwordFields.new) ? "text-green-600" : "text-gray-400"}>
                                            Une lettre minuscule
                                        </li>
                                        <li className={/\d/.test(passwordFields.new) ? "text-green-600" : "text-gray-400"}>
                                            Un chiffre
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Confirmation du mot de passe */}
                    <div>
                        <label htmlFor="confirm" className="block text-sm font-medium text-gray-700 mb-2">
                            Confirmer le nouveau mot de passe *
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FaLock className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                                id="confirm"
                                name="confirm"
                                type={showPasswords.confirm ? "text" : "password"}
                                value={passwordFields.confirm}
                                onChange={handlePasswordChange}
                                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-[--color-primary] transition-colors duration-200"
                                placeholder="Confirmez votre nouveau mot de passe"
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => togglePasswordVisibility('confirm')}
                            >
                                {showPasswords.confirm ? (
                                    <FaEyeSlash className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                                ) : (
                                    <FaEye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                                )}
                            </button>
                        </div>
                        {passwordFields.confirm && passwordFields.new !== passwordFields.confirm && (
                            <p className="mt-1 text-xs text-red-600">
                                Les mots de passe ne correspondent pas
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Message de statut */}
            {passwordMsg.text && (
                <div className={`flex items-center gap-3 p-4 rounded-lg border ${
                    passwordMsg.type === "success" 
                        ? "bg-green-50 border-green-200 text-green-800" 
                        : "bg-red-50 border-red-200 text-red-800"
                }`}>
                    {passwordMsg.type === "success" ? (
                        <FaCheck className="text-green-600" size={16} />
                    ) : (
                        <FaExclamationCircle className="text-red-600" size={16} />
                    )}
                    <span className="font-medium">{passwordMsg.text}</span>
                </div>
            )}

            {/* Bouton de sauvegarde */}
            <div className="flex justify-end pt-6 border-t border-gray-200">
                <button
                    type="submit"
                    className="flex items-center gap-2 px-6 py-3 bg-[--color-primary] text-white rounded-lg hover:bg-[--color-primary] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    disabled={passwordLoading}
                >
                    {passwordLoading ? (
                        <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Mise à jour...
                        </>
                    ) : (
                        <>
                            <FaShieldAlt size={16} />
                            Mettre à jour le mot de passe
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default Security;
